import { h } from 'preact';
import delve from 'dlv';
import { defaults } from '../config';
import { replaceFwdSlash } from '../lib/helpers/index';
import localTempStore from '../lib/dbee-client';
import * as weatherClient from '../lib/weather-client';

/**
 * This contains logic necessary to interact with the weather service, as well as
 * sanitizing the response for use on the front end. It is exported as a class in
 * order to facilitate testing, even though in general each function could be
 * exported by itself as it has no state dependencies.
 *
 */
export class WeatherModel {
	constructor(config = {}) {
		// TODO: Still hitting Prod on UAT
		Object.assign(this, { ...defaults, ...config });
		weatherClient.setApiOrigin(config.weatherServiceOrigin || defaults.weatherServiceOrigin);
	}


	_getLocalTempUnit = localTempStore.get;

	_setLocalTempUnit = localTempStore.set;

	setTemperatureScale = tempUnit => {
		this._setLocalTempUnit(tempUnit);
	};

	getTemperatureScale = () => {
		let { tempUnit } = this._getLocalTempUnit();
		return tempUnit;
	};

	/**
	 * Query the location service
	 *
	 * @param {string} geoId - Unique geoID value
	 * @returns {Promise<Object>}
	 */
	fetchLocationDataFromService = (geoId) => {
		let queryString = this.locationServiceOrigin.replace(/\/$/, '') + `/geonameid/${geoId}/`;
		return fetch(queryString).then(r => r.json());
	};

	/**
	 * Query the location service to find a US Zipcode from it's geoname ID value
	 *
	 * @param {string} geoId - Geo ID value, i.e. "5705957"
	 * @returns {Promise<string|null>}
	 */
	fetchUsZipFromGeo = geoId =>
		this.fetchLocationDataFromService(geoId).then(body => {
			let entry = body.documents[0];

			return entry.geoname_id;
		});

	fetchLocationFromGeo = geoId => (
		this.fetchLocationDataFromService(geoId)
			.then(entry => {
				if (typeof entry !== 'undefined') {
					return {
						latitude: entry.latitude,
						longitude: entry.longitude,
						city: entry.city_name,
						state: entry.subdivision_1_iso_code,
						country: entry.country_iso_code
					};
				}

				return null;
			})
	);

	fetchAlerts = ({ latitude, longitude, city, state, country }) => (
		weatherClient.fetchAlerts(latitude, longitude).then((data) => {
			if (data) {
				data.location = { city, state, country, latitude, longitude };
				return data;
			}
			console.error('No data given');
			return [];
		}).then(this._sanitizeAlerts).catch(e => null)
	);

	_sanitizeAlerts = data => {
		let alerts = data.alerts;
		if (!alerts || !alerts.length) return {};

		let description,
			color;

		// Set alerts based on order returned, with the first being
		// the highest severity and tally advisory types
		alerts.map((a, idx) => {
			if (alerts[idx].description) {
				// Build description or Alert + Advisory objects
				description = alerts[idx].description;
				color = alerts[idx].color;
			}
		});

		return {
			description,
			color,
			totalAlerts: alerts.length,
			alerts,
			location: data.location
		};
	};

	fetchCurrentConditions = (latitude, longitude) =>
		(weatherClient.fetchCurrentConditions(latitude, longitude)).then(this._sanitizeCurrentConditions);

	_sanitizeCurrentConditions = (current) => {
		let currentTemp = current.imperial.temp;
		let currentTempCels = Math.round(current.metric.temp);
		let conditions = replaceFwdSlash(current.description);
		let conditionDescription = current.description;
		let weatherIconCode = current.icon;
		let iconCodeNumeric = (current.icon) || '';
		let link = delve(current, '_links.provider_observations.href');

		return {
			currentTemp,
			currentTempCels,
			conditions,
			conditionDescription,
			weatherIconCode,
			iconCodeNumeric,
			link
		};
	};

	fetchAirQuality = (latitude, longitude) =>
		(weatherClient.fetchAirQuality(latitude, longitude));

}
